// react
import React, { useState } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { useMediaQuery, Grid, Typography, IconButton, Snackbar, Alert, Box, Paper, Slide, useTheme, Chip, Tooltip } from "@mui/material";
import {
  NotificationAdd as NotificationAddIcon,
  NotificationsNone as NotificationsNoneIcon,
  QrCode as QrCodeIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Settings as SettingsIcon,
  Stream as StreamIcon,
  CallReceived as CallReceivedIcon,
  AlarmAdd as AlarmAddIcon,
  AlarmOff as AlarmOffIcon,
  AccountCircle as AccountCircleIcon,
  Tag as TagIcon,
} from "@mui/icons-material";

// ui-components
import TagQrCodeDialog from "./TagQrCodeDialog";

// config
import documentTypeLabels from "../../../config/documentTypeLabels.json";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const Transition = (props) => <Slide {...props} direction="left" />;

const TagHeaderActionsCard = ({ databox, tag, isFollowing, handleFollowToggle, alarmOn, handleAlarmToggle, variant = "full" }) => {
  const { user, conservSostL1 } = UserAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [openQR, setOpenQR] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const showSnackbar = (message, severity) => {
    setSnackbarOpen(false);
    setTimeout(() => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    }, 100);
  };

  const handleFollowToggleWithSnackbar = async () => {
    const ok = await handleFollowToggle();
    if (!ok) return showSnackbar(t("follow_toggle_failed"), "error");

    const key = isFollowing ? (conservSostL1 ? "unfollow_success_archive" : "unfollow_success_databox") : conservSostL1 ? "follow_success_archive" : "follow_success_databox";

    showSnackbar(t(key), isFollowing ? "info" : "success");
  };

  const handleAlarmToggleWithSnackbar = async () => {
    const ok = await handleAlarmToggle();
    if (!ok) return showSnackbar(t("alarm_toggle_failed"), "error");

    showSnackbar(alarmOn ? t("alarm_disabled_success") : t("alarm_enabled_success"), alarmOn ? "info" : "success");
  };

  const getDocumentTypeLabel = (code) => documentTypeLabels[code] || code;

  const TagHeaderCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${theme.palette.background.default}F2)`,
        backdropFilter: "blur(10px)",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {databox?.name && (
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "0.3px" }}>
            {databox.name}
          </Typography>
        )}

        {conservSostL1 && databox?.tipologiaDocumentale && (
          <Chip
            label={getDocumentTypeLabel(databox.tipologiaDocumentale)}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
            }}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {databox?.id && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <TagIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
            <Typography variant="body2" color="text.secondary">
              {databox.id}
            </Typography>
          </Box>
        )}

        {tag?.firstseen && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <CallReceivedIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
            <Typography variant="body2" color="text.secondary">
              {convertTimestamp(tag.firstseen)}
            </Typography>
          </Box>
        )}

        {tag?.tagOwnerEmail && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <AccountCircleIcon sx={{ fontSize: 18, color: theme.palette.text.disabled }} />
            <Typography variant="body2" color="text.secondary">
              {tag.tagOwnerEmail}
            </Typography>
          </Box>
        )}

        {!conservSostL1 && (
          <Chip
            label="QR Code"
            icon={<QrCodeIcon sx={{ fontSize: 18, color: "inherit" }} />}
            onClick={() => setOpenQR(true)}
            size="small"
            clickable
            sx={{
              px: 0.5,
              borderRadius: 2,
              backgroundColor: `${theme.palette.primary.main}15`,
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}33`,
              "& .MuiChip-icon": {
                color: theme.palette.primary.main,
                mr: 0.4,
              },
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}25`,
                borderColor: `${theme.palette.primary.main}55`,
              },
            }}
          />
        )}
      </Box>

      {(databox?.description || tag?.notes) && (
        <Box
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: `${theme.palette.background.default}AA`,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.5,
              color: "text.primary",
              whiteSpace: "pre-line",
            }}
          >
            {databox.description || tag.notes}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          mt: 1,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: isMobile ? "center" : "flex-end",
          gap: 1.5,
        }}
      >
        <Tooltip title={conservSostL1 ? t("documents") : t("certifications")} placement="top">
          <IconButton onClick={() => navigate(`/${tag.id}`)}>
            <PlaylistAddCheckIcon color="primary" fontSize="large" />
          </IconButton>
        </Tooltip>

        {tag?.tagOwner === user.uid && (
          <Tooltip title={t("settings")} placement="top">
            <IconButton onClick={() => navigate(`/${tag.id}/settings`)}>
              <SettingsIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        )}

        {!conservSostL1 && (
          <>
            <Tooltip title={t("monitor")} placement="top">
              <IconButton onClick={() => navigate(`/${tag.id}/monitor`)}>
                <StreamIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("notifiche")} placement="top">
              <IconButton onClick={handleFollowToggleWithSnackbar}>
                {isFollowing ? <NotificationsNoneIcon color="primary" fontSize="large" /> : <NotificationAddIcon color="primary" fontSize="large" />}
              </IconButton>
            </Tooltip>

            <Tooltip title={t("alarms")} placement="top">
              <IconButton onClick={handleAlarmToggleWithSnackbar}>{alarmOn ? <AlarmOffIcon color="primary" fontSize="large" /> : <AlarmAddIcon color="primary" fontSize="large" />}</IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Paper>
  );

  if (variant === "compact") {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 4,
          background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${theme.palette.background.default}F2)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: "0.3px" }}>
          {databox?.name || "-"}
        </Typography>

        <Box>
          <Tooltip title={conservSostL1 ? t("documents") : t("certifications")} placement="top">
            <IconButton onClick={() => navigate(`/${tag.id}`)}>
              <PlaylistAddCheckIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>

          {tag?.tagOwner === user.uid && (
            <Tooltip title={t("settings")} placement="top">
              <IconButton onClick={() => navigate(`/${tag.id}/settings`)}>
                <SettingsIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          )}

          {!conservSostL1 && (
            <Tooltip title={t("monitor")} placement="top">
              <IconButton onClick={() => navigate(`/${tag.id}/monitor`)}>
                <StreamIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <TagHeaderCard />
        </Grid>
      </Grid>

      {!conservSostL1 && <TagQrCodeDialog tag={tag.id} open={openQR} setOpen={setOpenQR} />}

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} TransitionComponent={Transition} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert
          severity={snackbarSeverity}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          sx={{
            width: "100%",
            borderRadius: 1.5,
            textTransform: "none",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TagHeaderActionsCard;
